/**
 * Area Page Observability Helper Utilities
 *
 * Pure utility functions for area page observability configuration and validation.
 */

import type { Tenant } from '@tenants/types';
import { AREA_PAGE_OBSERVABILITY_DEFAULTS } from 'observability/constants/area-page-events';

/**
 * Check if area page observability is enabled for a tenant
 *
 * Checks both global observability toggle and area page specific toggle.
 */
export function isAreaPageObservabilityEnabled(tenant: Tenant<any, any, any, any>): boolean {
  const observabilityEnabled =
    tenant.config?.observability?.enabled ?? AREA_PAGE_OBSERVABILITY_DEFAULTS.OBSERVABILITY_ENABLED;

  const areaPagesEnabled =
    tenant.config?.observability?.areaPages?.enabled ?? AREA_PAGE_OBSERVABILITY_DEFAULTS.AREA_PAGES_ENABLED;

  return observabilityEnabled && areaPagesEnabled;
}

/**
 * Check if a city is marked as critical in tenant configuration
 *
 * @param citySlug - City slug to check (e.g., 'toronto-on')
 * @param tenant - Tenant configuration
 * @returns true if city is in critical cities list
 */
export function isCriticalCity(citySlug: string, tenant: Tenant<any, any, any, any>): boolean {
  const criticalCities =
    tenant.config?.observability?.areaPages?.criticalCities ?? AREA_PAGE_OBSERVABILITY_DEFAULTS.CRITICAL_CITIES;

  return criticalCities.includes(citySlug);
}

/**
 * Get critical cities list from tenant configuration
 *
 * @param tenant - Tenant configuration
 * @returns Array of critical city slugs
 */
export function getCriticalCities(tenant: Tenant<any, any, any, any>): string[] {
  return tenant.config?.observability?.areaPages?.criticalCities ?? AREA_PAGE_OBSERVABILITY_DEFAULTS.CRITICAL_CITIES;
}

